/**
 * Load-time validation of the app-only FIELD-3 records (PRP-SKETCH-FIELD-3 re-review
 * item 1: never hard-fail). A stored plan is data from a device, sync or an older
 * build; one bad value in a record must not stop the plan opening, inject openings or
 * numbers Restore wall cannot place, or break id numbering. So every record is checked
 * on load (`planFromStoredRooms`) and invalid entries are dropped; the room still opens.
 *
 * - `missing.removedOpenings` (W2): each entry a room in the plan, a whole index, and a
 *   well-formed opening (a known kind, an id and link that are whole numbers or strings,
 *   offset and width finite and within `export.max_plan_ft`, a door / window / missing
 *   object when present). An id or link a live opening already uses is dropped too.
 * - `missing.absorbed` (J1): each entry a room in the plan, `distanceIn` finite within
 *   0..`export.wall_thickness_max_in`, `wallIn` (when present) likewise. One bad entry
 *   drops the whole record and marks the missing wall `recordDropped`: Restore wall
 *   then says it cannot give the rooms back instead of guessing (DD-SF3.20).
 * - measurements: `valueFt` finite within `max_plan_ft` (else the measurement goes);
 *   `adjustIn` / `baseFt` (J3) finite and within bounds, else both go (the reading stays);
 *   `source` (FIELD-4 K14) one of `MEASUREMENT_SOURCES`, else it goes (the reading is manual).
 * - `walls` thickness entries within 0..`wall_thickness_max_in`; `attachedEdge` a corner
 *   of the room; `spans` measurement-shaped; `checkDims` `{ reason, at }` strings.
 */
import { IN_PER_FT, MEASUREMENT_SOURCES, POLICY } from './policy';
import type { AbsorbedV2, Measurement, MissingWallV2, Opening, Plan, RemovedOpeningV2, Room } from './types';

const KINDS = new Set<string>(['door', 'window', 'missing']);
const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const finite = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const maxFt = () => POLICY.export.max_plan_ft;
const maxWallIn = () => POLICY.export.wall_thickness_max_in;
const inRange = (v: unknown, lo: number, hi: number): v is number => finite(v) && v >= lo && v <= hi;

/** An id the core can number around: a whole number (safe integer, not negative) or a non-empty string. */
export const validId = (v: unknown): boolean =>
  (typeof v === 'number' && Number.isSafeInteger(v) && v >= 0) || (typeof v === 'string' && v.length > 0);
/** A numeric id the id counter may count (`reindexNext`). */
export const countableId = (v: unknown): v is number => typeof v === 'number' && Number.isSafeInteger(v) && v >= 0;

/** A well-formed opening (shape only; whether it fits a wall is Restore wall's check). */
export function validOpening(o: unknown): o is Opening {
  if (!isObj(o)) return false;
  if (typeof o.kind !== 'string' || !KINDS.has(o.kind) || !validId(o.id) || !validId(o.edgeStart)) return false;
  if (o.link != null && !validId(o.link)) return false;
  if (!inRange(o.offFt, 0, maxFt()) || !inRange(o.widthFt, 0, maxFt()) || o.widthFt <= 0) return false;
  if (o.heightFt != null && (!inRange(o.heightFt, 0, maxFt()) || o.heightFt <= 0)) return false;
  if (o.door != null && !(isObj(o.door) && typeof o.door.type === 'string')) return false;
  if (o.window != null && !isObj(o.window)) return false;
  if (o.missing != null && !isObj(o.missing)) return false;
  return true;
}

function validAbsorbed(x: unknown, roomIds: Set<string>): x is AbsorbedV2 {
  if (!isObj(x) || typeof x.roomId !== 'string' || !roomIds.has(x.roomId)) return false;
  if (!inRange(x.distanceIn, 0, maxWallIn())) return false;
  if (x.bump != null && !(Array.isArray(x.bump) && x.bump.every(validId))) return false;
  return x.wallIn == null || inRange(x.wallIn, 0, maxWallIn());
}

const validCheckDims = (v: unknown): boolean => isObj(v) && typeof v.reason === 'string' && typeof v.at === 'string';

function cleanAbsorbedEntry(x: AbsorbedV2): AbsorbedV2 {
  const out: AbsorbedV2 = { roomId: x.roomId, distanceIn: x.distanceIn };
  if (x.wallIn != null) out.wallIn = x.wallIn;
  if (typeof x.note === 'string') out.note = x.note;
  if (x.prevNote != null && validCheckDims(x.prevNote)) out.prevNote = x.prevNote;
  if (x.bump?.length) out.bump = [...x.bump];
  return out;
}

/** What the sanitizer dropped, for tests and logs. */
export interface SanitizeReport { dropped: string[] }

function sanitizeMissing(
  m: MissingWallV2, where: string, roomIds: Set<string>, liveIds: Set<unknown>, rep: SanitizeReport,
): void {
  if (m.absorbed !== undefined) {
    const a: unknown = m.absorbed;
    if (Array.isArray(a) && a.every((x) => validAbsorbed(x, roomIds))) m.absorbed = a.map(cleanAbsorbedEntry);
    else {
      delete m.absorbed;
      m.recordDropped = true;
      rep.dropped.push(`${where} absorbed`);
    }
  }
  if (m.removedOpenings !== undefined) {
    const list: unknown = m.removedOpenings;
    if (!Array.isArray(list)) {
      delete m.removedOpenings;
      rep.dropped.push(`${where} removedOpenings`);
      return;
    }
    const seen = new Set<unknown>();
    const kept: RemovedOpeningV2[] = [];
    list.forEach((x: unknown, i) => {
      const ok = isObj(x) && typeof x.roomId === 'string' && roomIds.has(x.roomId)
        && typeof x.index === 'number' && Number.isSafeInteger(x.index) && x.index >= 0
        && validOpening(x.opening) && !liveIds.has(x.opening.id) && !seen.has(x.opening.id)
        && (x.opening.link == null || !liveIds.has(x.opening.link));
      if (!ok) { rep.dropped.push(`${where} removedOpenings[${i}]`); return; }
      const e = x as unknown as RemovedOpeningV2;
      seen.add(e.opening.id);
      if (e.opening.missing) sanitizeMissing(e.opening.missing, `${where} removedOpenings[${i}]`, roomIds, liveIds, rep);
      kept.push(e);
    });
    if (kept.length) m.removedOpenings = kept; else delete m.removedOpenings;
  }
}

function measurementShaped(m: unknown): m is Measurement {
  return isObj(m) && (m.axis === 'x' || m.axis === 'y') && validId(m.a) && validId(m.b)
    && inRange(m.valueFt, -maxFt(), maxFt()) && (m.kind === 'len' || m.kind === 'across');
}

/** J3's record on a measurement: kept only when both halves are sane. */
function sanitizeAdjust(m: Measurement, where: string, rep: SanitizeReport): void {
  const adjOk = m.adjustIn === undefined || inRange(m.adjustIn, -maxFt() * IN_PER_FT, maxFt() * IN_PER_FT);
  const baseOk = m.baseFt === undefined || (inRange(m.baseFt, -maxFt(), maxFt()) && m.adjustIn !== undefined);
  if (adjOk && baseOk) return;
  delete m.adjustIn;
  delete m.baseFt;
  rep.dropped.push(`${where} adjust`);
}

function sanitizeRoom(r: Room, roomIds: Set<string>, liveIds: Set<unknown>, rep: SanitizeReport): void {
  const keepMeas: Measurement[] = [];
  (Array.isArray(r.measurements) ? r.measurements : []).forEach((m, i) => {
    if (!measurementShaped(m)) { rep.dropped.push(`${r.id} measurement[${i}]`); return; }
    sanitizeAdjust(m, `${r.id} measurement[${i}]`, rep);
    if (m.source !== undefined && !MEASUREMENT_SOURCES.includes(m.source)) {
      delete m.source;
      rep.dropped.push(`${r.id} measurement[${i}] source`);
    }
    keepMeas.push(m);
  });
  r.measurements = keepMeas;
  if (r.spans !== undefined) {
    const s: unknown = r.spans;
    const kept = Array.isArray(s) ? s.filter(measurementShaped) : [];
    if (!Array.isArray(s) || kept.length !== s.length) rep.dropped.push(`${r.id} spans`);
    kept.forEach((m, i) => sanitizeAdjust(m, `${r.id} span[${i}]`, rep));
    if (kept.length) r.spans = kept; else delete r.spans;
  }
  if (r.attachedEdge !== undefined && !r.corners.some((c) => c.id === r.attachedEdge)) {
    delete r.attachedEdge;
    rep.dropped.push(`${r.id} attachedEdge`);
  }
  if (r.checkDims !== undefined && !validCheckDims(r.checkDims)) {
    delete r.checkDims;
    rep.dropped.push(`${r.id} checkDims`);
  }
  if (r.walls !== undefined) {
    if (!isObj(r.walls)) {
      delete r.walls;
      rep.dropped.push(`${r.id} walls`);
    } else {
      for (const [k, w] of Object.entries(r.walls)) {
        if (isObj(w) && inRange(w.thicknessIn, 0, maxWallIn())) continue;
        delete (r.walls as Record<string, unknown>)[k];
        rep.dropped.push(`${r.id} walls[${k}]`);
      }
    }
  }
  for (const o of r.openings) {
    if (o.kind === 'missing' && isObj(o.missing)) sanitizeMissing(o.missing, `${r.id} opening ${String(o.id)}`, roomIds, liveIds, rep);
  }
}

/** Validate every app-only record of the plan in place (see the module comment). */
export function sanitizePlan(plan: Plan): SanitizeReport {
  const rep: SanitizeReport = { dropped: [] };
  const roomIds = new Set(plan.rooms.map((r) => r.id));
  const liveIds = new Set<unknown>();
  plan.rooms.forEach((r) => r.openings.forEach((o) => { liveIds.add(o.id); if (o.link != null) liveIds.add(o.link); }));
  plan.rooms.forEach((r) => sanitizeRoom(r, roomIds, liveIds, rep));
  return rep;
}
